import { debounce } from 'lodash'
import { observer } from 'mobx-react-lite'
import React, { useEffect } from 'react'
import { ActivityIndicator, FlatList, Image, StatusBar, TouchableOpacity, View } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'

import { useRouter } from 'expo-router'

import PracticeIcon from '../../../assets/practiceScreen/practiceGameIcon.png'
import { Text } from '../../components'
import { MainHeader } from '../../components/MainHeader/MainHeader'
import { useCreateGame } from '../../hooks/use-create-game'
import { useStore } from '../../models'
import { GameModeTag, TournamentAccessControl, TournamentDifficulty } from '../../models/api-store'
import { getRouteToLastVisitedTab } from '../../navigation'
import { verticalScale } from '../../styles/sizes'
import { extendLogger, LoggerContexts } from '../../utils/logger'
import { styles } from './styles'
import { WebWrapper } from '@/components/WebWrapper'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

const logger = extendLogger(LoggerContexts.LobbyScreen)
const BOT_LIST = [1, 2, 3, 4, 5, 6, 7, 8]
const BLIND_LIST = [0, 4, 8, 12, 15]

export const PracticeGameScreen = observer(() => {
  const router = useRouter()
  const insets = useSafeAreaInsets()

  const {
    game: { botsCount, setBotsCount, setGameKind, setPlayKind, setTableName },
    session: { selectedDailyTask, updateDailyTaskStatus, lastVisitedTab }
  } = useStore()
  const [tournamentBlindsLength, setTournamentBlindsLength] = React.useState(0)

  const { loading, setLoading, createGame } = useCreateGame()

  useEffect(() => {
    setBotsCount(0)
  }, [setBotsCount])

  const createPracticeGame = async ({ isTournament }: { isTournament: boolean }) => {
    try {
      const gameKind = isTournament ? GameModeTag.practiceTournament : GameModeTag.practice
      setGameKind(gameKind)
      await createGame(
        {
          accessControl: TournamentAccessControl.private,
          gameKind
        },
        isTournament
          ? {
              tournamentName: 'Table 1',
              tournamentBlindsLength: tournamentBlindsLength.toString(),
              tournamentActionTime: '',
              tournamentStartingChips: '',
              tournamentDifficulty: TournamentDifficulty.GENERAL,
              accessControl: TournamentAccessControl.private
            }
          : undefined
      )
      setTimeout(() => {
        setLoading(false)
        router.navigate('/game')
      }, 1000)
    } catch (error) {
      logger.error('Error in creating tournament', error)
      setLoading(false)
    }
  }

  const onCreateGame = debounce(() => {
    setLoading(true)
    setTableName('')
    setPlayKind('player')
    createPracticeGame({ isTournament: tournamentBlindsLength >= 1 })
  }, 250)

  const onBack = () => {
    if (router.canDismiss()) {
      return router.dismiss()
    }
    router.dismissTo(getRouteToLastVisitedTab(lastVisitedTab))
  }

  const renderBots = ({ item }: { item: number }) => {
    return (
      <TouchableOpacity
        onPress={() => setBotsCount(item)}
        style={[styles.botView, { borderColor: item === botsCount ? '#121212' : '#12121233' }]}
      >
        <Text style={styles.botText}>{item}</Text>
      </TouchableOpacity>
    )
  }

  const renderBlinds = ({ item }: { item: number }) => {
    if (item === 0) {
      return (
        <TouchableOpacity
          onPress={() => setTournamentBlindsLength(item)}
          style={[styles.botView, { borderColor: tournamentBlindsLength < 1 ? '#121212' : '#12121233' }]}
        >
          <Text
            tx="common.no"
            style={[
              styles.noText,
              {
                borderColor: tournamentBlindsLength < 1 ? '#121212' : '#12121233'
              }
            ]}
          />
        </TouchableOpacity>
      )
    }
    return (
      <TouchableOpacity
        onPress={() => setTournamentBlindsLength(item)}
        style={[styles.botView, { borderColor: item === tournamentBlindsLength ? '#121212' : '#12121233' }]}
      >
        <Text style={[styles.botText]}>{item}</Text>
      </TouchableOpacity>
    )
  }

  return (
    <View
      style={[
        styles.container,
        {
          paddingBottom: insets.bottom,
          paddingTop: insets.top
        }
      ]}
    >
      <StatusBar barStyle="dark-content" />
      <WebWrapper>
        <MainHeader
          hideLogo
          leftPress={onBack}
          leftIconStyle={styles.closeIconStyle}
          headerContainerStyle={{ marginTop: StatusBar.currentHeight }}
        />
        <View style={styles.mainContainer}>
          <View style={{ flex: 8, alignItems: 'center', justifyContent: 'center' }}>
            <Image source={PracticeIcon} resizeMode="contain" style={styles.PracticeIcon} />
            <Text tx={'practiceGameScreen.title'} style={styles.title} />
            <Text tx={'practiceGameScreen.description'} style={styles.description} />
            <View style={styles.botContainer}>
              <FlatList
                data={BOT_LIST}
                numColumns={4}
                renderItem={renderBots}
                keyExtractor={(item) => `bots-${item}`}
                showsHorizontalScrollIndicator={false}
                bounces={false}
              />
            </View>
            <View style={styles.checkboxContainer}>
              <Text tx={'practiceGameScreen.blindsIncrease'} style={styles.blindText} />
              <Text tx={'practiceGameScreen.blindSelectionMsg'} style={[styles.blindText, { fontSize: 14 }]} />
            </View>
            <View style={[styles.botContainer, { marginTop: 0 }]}>
              <FlatList
                horizontal={true}
                data={BLIND_LIST}
                renderItem={renderBlinds}
                keyExtractor={(item, index) => `bots-${index}`}
                showsHorizontalScrollIndicator={false}
                bounces={false}
              />
            </View>
          </View>
          <View style={{ flex: 1, alignItems: 'flex-end', gap: verticalScale(50) }}>
            <TouchableOpacity
              disabled={(botsCount < 1 && tournamentBlindsLength < 1) || (botsCount < 1 && tournamentBlindsLength > 0)}
              onPress={onCreateGame}
              style={[styles.buttonStyle, { opacity: botsCount < 1 ? 0.5 : 1 }]}
            >
              <View style={styles.buttonView}>
                <Text tx={'practiceGameScreen.play'} style={styles.buttonText} />
              </View>
              {loading ? (
                <View style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <ActivityIndicator size="small" color="#FFFFFF" style={styles.loadingStyle} />
                </View>
              ) : null}
            </TouchableOpacity>
          </View>
        </View>
      </WebWrapper>
    </View>
  )
})
